import { AppComponent, CodeEditor, CodeEditorAdapter, CodeEditorOptions } from "@reporting/rp-app-host";
import type { Saveable } from "@reporting/rp-app-host";
import { customElement, property, query } from "lit/decorators.js";
import { css, html } from "lit";
import { ColorTypes, ComponentId, SplitContainer, actions, dom } from "@omegagrid/core";
import { Resource } from "@reporting/rp-types/ext";
import { msg } from "@omegagrid/localize";
import { debounce } from "ts-debounce";
import { ExtensionSandboxHost } from "./extensionSandboxHost";

const REFRESH_TIMEOUT = 1000;

export abstract class HtmlEditorAdapter extends CodeEditorAdapter<CodeEditor> {

	abstract getResource() : Resource|Promise<Resource>;

	createOptions(options?: CodeEditorOptions): CodeEditorOptions {
		return super.createOptions({
			language: 'html',
			toolbar: {
				preview: {
					id: 'refreshPreview',
					type: 'dropdown-button',
					width: 90,
					items: [{
						k: 'reloadPreview',
						v: msg('Reload'),
						icon: 'refresh'
					}],
					text: msg('Refresh'),
					color: ColorTypes.Gray,
					icon: 'arrows-rotate',
					enabled: true,
					ord: 0
				}
			},
			...options
		});
	}

}

@customElement('rp-editor-html')
export class HtmlEditor extends AppComponent implements Saveable {

	static styles = [AppComponent.styles, css`
		:host {
			height: 100%;
		}

		og-splitcontainer {
			height: 100%;
		}
	`];

	@property({type: Object})
	adapter: HtmlEditorAdapter;

	@query('og-splitcontainer')
	splitContainer: SplitContainer;

	private _editor: CodeEditor;
	get editor() {
		if (this._editor) return this._editor;
		return (this._editor = dom.createElement<CodeEditor>('rp-editor-code', {
			adapter: this.adapter,
			listeners: [
				['toolbar.action', this._onToolbarAction],
				['code.change', () => this._debounceRefresh()],
			]
		}));
	}

	private _preview: ExtensionSandboxHost;
	async getPreview() {
		if (this._preview) return this._preview;
		return (this._preview = dom.createElement<ExtensionSandboxHost>('rp-extensionhost-sandbox', {
			resource: await this.adapter?.getResource(),
			container: this.container
		}));
	}

	updated(props: Map<string, unknown>) {
		if (props.has('adapter')) {
			this._preview = null;
		}
	}

	layout() {
		this.splitContainer?.layout();
	}

	_onToolbarAction = (e: actions.ActionEvent) => {
		if (e.action.type != 'select') return;

		if (e.action.key == 'refreshPreview') {
			this.refreshPreview();
		} else if (e.action.key == 'reloadPreview') {
			this._preview?.reload(this.editor?.editor?.content);
		} else if (e.action.key == 'discard') {
			this._debounceRefresh();
		}
	}

	refreshPreview() {
		this._preview?.refresh(this.editor?.editor?.content);
	}

	_debounceRefresh = debounce(() => this.refreshPreview(), REFRESH_TIMEOUT);

	render = () => html`
		<og-splitcontainer
			gap="6"
			direction="vertical"
			.items="${[
				{id: 'editor'},
				{id: 'preview', size: 400}
			]}"
			.createComponent="${(id: ComponentId) => id == 'editor' ? this.editor : this.getPreview() }">
		</og-splitcontainer>
	`;

	async save() {
		await this.editor?.save();
	}
}
